import type { DropdownMenu as RadixDropdownMenu } from "radix-ui";
import { BlankSlateInline } from "../blank-slate";
import { icon } from "../icons";
import { useTranslation } from "../localization";
import { cn } from "../utils";
import { useMenuContext } from "./context";
import { MenuItem, type MenuItemType } from "./menu-item";

/** Props Interface */
interface MenuOptionsProps {
	items: Array<MenuItemType | string | "divider" | null> | undefined;
	type: "root" | "sub";
	maxHeight?: number;
}

export function MenuOptions({ items, type, maxHeight = 500 }: MenuOptionsProps) {
	/** Context */
	const { TypeComponent, side, align } = useMenuContext();

	/** Hooks */
	const { t } = useTranslation();

	/** Computed */
	// The cast bridges the flavor union to its widest member: the context-menu flavor does not
	// declare the positioning props (`side`, `align`) it ignores at runtime — a context menu
	// positions at the pointer, and every flavor tolerates the dropdown-shaped props.
	const Component = (
		type === "root" ? TypeComponent.Content : TypeComponent.SubContent
	) as typeof RadixDropdownMenu.Content;

	/** Render */
	return (
		<Component
			style={{ maxHeight }}
			className="listbox-options h-max w-max! min-w-[300px]! z-504 overflow-y-auto"
			sideOffset={type === "root" ? 10 : 5}
			alignOffset={0}
			loop
			collisionPadding={20}
			side={side}
			align={align}
		>
			{!items?.length ? (
				<div className="listbox-option cursor-default">
					<BlankSlateInline
						Icon={icon("CircleSlash")}
						label={t("general.no_menu_items_available")}
					/>
				</div>
			) : null}
			{items?.map((item) =>
				!item ? null : item === "divider" ? (
					<TypeComponent.Separator
						key={Math.random()}
						className={cn("w-full bg-primary-100 dark:bg-primary-800 h-px self-center my-2")}
					/>
				) : typeof item === "string" ? (
					<TypeComponent.Label
						key={item}
						className="label px-2.5 py-2"
					>
						{item}
					</TypeComponent.Label>
				) : (
					<MenuItem
						key={item.label}
						{...item}
						maxHeight={maxHeight}
					/>
				),
			)}
		</Component>
	);
}
